<script setup lang="ts">
import { computed } from "vue"

import router from "@/plugins/router"

const props = defineProps({
  type: { type: String, default: "button" },
  text: { type: String, required: true },
  color: { type: String, default: "primary" },
  size: { type: String, default: "md" },
  path: { type: String, default: "#" },
  external: { type: Boolean, default: false },
  download: { type: Boolean, default: false },
  modal: String,
  isSubmitting: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
})

const emits = defineEmits(["click"])

const colorClass = computed(() => {
  return `btn-${props.color}`
})
const sizeClass = computed(() => {
  return `btn-${props.size}`
})

const isDisabled = computed(() => {
  return props.disabled || props.isSubmitting
})

// only fire router.push when it's a plain click
function linkClick(e: MouseEvent) {
  // if they held cmd/ctrl/shift/alt or did a middle-click, let the browser handle it
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0 || props.external) {
    return
  }
  e.preventDefault()
  emits("click", e)
  router.push(props.path)
}
</script>

<template>
  <button
    v-if="type === 'button'"
    type="button"
    v-bind="$attrs"
    class="btn"
    :class="[colorClass, sizeClass]"
    :disabled="isDisabled"
    @click="emits('click')"
    :data-bs-toggle="modal ? 'modal' : ''"
    :data-bs-target="modal ? `#${modal}` : ''"
  >
    <span v-if="!isSubmitting">{{ text }}</span>
    <span v-if="isSubmitting" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
  </button>
  <button
    v-if="type === 'submit'"
    type="submit"
    v-bind="$attrs"
    class="btn"
    :class="[colorClass, sizeClass]"
    :disabled="isDisabled"
    @click="emits('click')"
  >
    <span v-if="!isSubmitting">{{ text }}</span>
    <span v-if="isSubmitting" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
  </button>
  <a
    v-if="type === 'link'"
    v-bind="$attrs"
    :href="path"
    class="btn"
    :class="[colorClass, sizeClass, { 'p-0': color === 'link' }, { disabled: isDisabled }]"
    @click="linkClick"
    :target="external ? '_blank' : undefined"
  >
    <span v-if="!isSubmitting">{{ text }}</span>
    <span v-if="isSubmitting" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
  </a>
</template>

<style scoped></style>
